import { useCallback, useState } from "react";
import { useAppDispatch } from "store/hooks";
import { attachmentDownload } from "store/slices/documentsSlice";
import { downloadFileFromURL } from "utils/HelperFunctions";
import { DocumentAttachment } from "types/Documents";
import {
  DOWNLOAD_SUCCESS_MESSAGE,
  DOWNLOAD_ERROR_MESSAGE,
} from "constants/documents";
import { DocumentFilter } from "types/Documents";
import { DOCUMENTS_DEFAULT_OFFSET, DOCUMENTS_PAGE_SIZE } from "constants/documents";
import { ToastState } from "types/common";


// useDocumentDownload - Handle download logic


const useDocumentDownload = (
  onToastMessage: (msg: ToastState) => void
) => {
  const dispatch = useAppDispatch();
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  const handleDownload = useCallback(
    async (path: string, fileName: string, id: string) => {
      setIsDownloading(id);
      try {
        const payload = { fileName: path };
        const res: any = await dispatch(attachmentDownload(payload)).unwrap();

        if (res?.body?.success) {
          const result = await downloadFileFromURL(res.body.data, fileName);
          if (result) {
            onToastMessage({
              variant: "success",
              content: DOWNLOAD_SUCCESS_MESSAGE(fileName),
              isFromPreview: false,
              nonce: Date.now(),
            });
          } else {
            onToastMessage({
              variant: "error",
              content: DOWNLOAD_ERROR_MESSAGE(fileName),
              isFromPreview: false,
              nonce: Date.now(),
            });
          }
        } else {
          onToastMessage({
            variant: "error",
            content: DOWNLOAD_ERROR_MESSAGE(fileName),
            isFromPreview: false,
            nonce: Date.now(),
          });
        }
      } catch (error: any) {
        onToastMessage({
          variant: "error",
          content: DOWNLOAD_ERROR_MESSAGE(fileName),
          isFromPreview: false,
          nonce: Date.now(),
        });
        console.error("attachment download error:", error?.message);
      } finally {
        setIsDownloading(null);
      }
    },
    [dispatch, onToastMessage]
  );

  return { isDownloading, handleDownload };
};


// useDocumentPreview - Handle preview state


const useDocumentPreview = () => {
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<DocumentAttachment | null>(
    null
  );
  const [previewId, setPreviewId] = useState<string>("");

  const openPreview = useCallback((data: DocumentAttachment, id?: string) => {
    setPreviewData(data);
    setIsPreview(true);
    if (id) setPreviewId(id);
  }, []);

  const closePreview = useCallback(() => {
    setIsPreview(false);
    setPreviewData(null);
    setPreviewId("");
  }, []);

  return {
    isPreview,
    previewData,
    previewId,
    openPreview,
    closePreview,
    setIsPreview,
    setPreviewData,
    setPreviewId,
  };
};


const useDocumentFilters = () => {
  const [filter, setFilter] = useState<DocumentFilter>({
    offset: DOCUMENTS_DEFAULT_OFFSET,
    limit: DOCUMENTS_PAGE_SIZE,
  });

  const handleNextPage = useCallback(() => {
    setFilter((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, []);

  const handlePreviousPage = useCallback(() => {
    setFilter((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  }, []);

  const resetFilter = useCallback(() => {
    setFilter({
      offset: DOCUMENTS_DEFAULT_OFFSET,
      limit: DOCUMENTS_PAGE_SIZE,
    });
  }, []);

  return {
    filter,
    setFilter,
    handleNextPage,
    handlePreviousPage,
    resetFilter,
  };
};


// useDocumentActions - Combined actions


export const useDocumentActions = () => {
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [toastMessage, setToastMessage] = useState<ToastState>({
    variant: "",
    content: null,
    isFromPreview: false,
  });

  const { isDownloading, handleDownload } = useDocumentDownload(setToastMessage);
  const preview = useDocumentPreview();
  const filters = useDocumentFilters();

  const clearToast = useCallback(() => {
    setToastMessage({
      variant: "",
      content: null,
      isFromPreview: false,
    });
  }, []);

  return {
    isTableLoading,
    setIsTableLoading,
    toastMessage,
    setToastMessage,
    isDownloading,
    handleDownload,
    clearToast,
    ...preview,
    ...filters,
  };
};
